// utils/judgeCalculator.ts - Separate TypeScript-Datei für Frontend

// 🔹 TypeScript Interfaces
export interface JudgeRule {
  type: string; // String statt "Elite" | "Basis" für Kompatibilität
  minAthletes: number;
  maxAthletes: number;
  requiredJudges: number;
}

export interface PenaltyFees {
  elite: number;
  basic: number;
}

export interface PenaltyFeeCaps {
  elite: number | null;
  basic: number | null;
}

export interface AthleteRegistration {
  _id: string;
  competition: string;
  club: string;
  athlete: {
    _id: string;
    firstname: string;
    surname: string;
    age: number;
    gender: string;
  };
  competitionGroup?: string;
  fee?: number;
}

export interface CompetitionGroup {
  _id: string;
  type: string; // String statt "Elite" | "Basis" für Kompatibilität
  name?: string; // Optional hinzugefügt, falls in der TSX-Datei verwendet
  // ... andere Felder
}

export interface JudgeRegistration {
  _id: string;
  competition: string;
  club: string;
  judge: {
    _id: string;
    firstname: string;
    surname: string;
    license: string;
  };
  position: string[];
  type: string; // String statt "Elite" | "Basis" für Kompatibilität
}

export interface AthleteCountData {
  eliteAthletes: number;
  basicAthletes: number;
  eliteJudges: number;
  basicJudges: number;
}

export interface PenaltyCalculationResult {
  totalPenaltyFeeElite: number;
  totalPenaltyFeeBasic: number;
  totalPenaltyFee: number;
}

// 🔹 Hilfsfunktion: Zähle Athleten und Kampfrichter
export function countAthletesAndJudges(
  athletes: AthleteRegistration[],
  judges: JudgeRegistration[],
  competitionGroups: CompetitionGroup[]
): AthleteCountData {
  const eliteAthletes = athletes.filter((ath) => {
    const group = competitionGroups.find((g) => g._id === ath.competitionGroup);
    return group?.type === "Elite";
  }).length;

  const basicAthletes = athletes.filter((ath) => {
    const group = competitionGroups.find((g) => g._id === ath.competitionGroup);
    return group?.type === "Basis";
  }).length;

  const eliteJudges = judges.filter((j) => j.type === "Elite").length;
  const basicJudges = judges.filter((j) => j.type === "Basis").length;

  return {
    eliteAthletes,
    basicAthletes,
    eliteJudges,
    basicJudges
  };
}

// 🔹 Kernfunktion: Berechne Strafgebühren für einen Typ
export function calculatePenaltyFees(
  athletes: number,
  judges: number,
  judgeRules: JudgeRule[],
  ruleType: "Elite" | "Basis",
  penaltyFee: number,
  cap?: number | null
): number {
  if (athletes === 0) return 0;
  
  // Finde alle Regeln für den gewünschten Typ und sortiere sie
  const typeRules = judgeRules
    .filter(rule => rule.type === ruleType)
    .sort((a, b) => a.minAthletes - b.minAthletes);
  
  if (typeRules.length === 0) return 0;
  
  let uncoveredAthletes = 0;
  
  if (judges === 0) {
    // Keine Kampfrichter vorhanden - alle Sportler zahlen
    uncoveredAthletes = athletes;
  } else {
    // Berechne bis zu welchem Sportler die vorhandenen Kampfrichter abdecken
    let lastCoveredAthlete = 0;
    
    // Jeder Kampfrichter deckt die Sportler einer Regel ab
    // KR 1 → Regel 1, KR 2 → Regel 2, etc.
    for (let i = 0; i < Math.min(judges, typeRules.length); i++) {
      lastCoveredAthlete = typeRules[i].maxAthletes;
    }
    
    // Sportler über der Abdeckung zahlen Strafe
    uncoveredAthletes = Math.max(0, athletes - lastCoveredAthlete);
  }
  
  const totalFee = uncoveredAthletes * penaltyFee;

  if (cap !== null && cap !== undefined && totalFee > cap) {
    return cap;
  }

  return totalFee;
}

// 🔹 Hauptfunktion: Berechne alle Strafgebühren
export function calculateAllPenaltyFees(
  athleteData: AthleteCountData,
  judgeRules: JudgeRule[],
  penaltyFees: PenaltyFees,
  penaltyFeeCaps?: Partial<PenaltyFeeCaps>
): PenaltyCalculationResult {
  const { eliteAthletes, basicAthletes, eliteJudges, basicJudges } = athleteData;

  // Berechne Strafgebühren für Elite
  const totalPenaltyFeeElite = calculatePenaltyFees(
    eliteAthletes,
    eliteJudges,
    judgeRules,
    "Elite",
    penaltyFees.elite,
    penaltyFeeCaps?.elite
  );

  // Berechne Strafgebühren für Basis
  const totalPenaltyFeeBasic = calculatePenaltyFees(
    basicAthletes,
    basicJudges,
    judgeRules,
    "Basis",
    penaltyFees.basic,
    penaltyFeeCaps?.basic
  );
  
  // Gesamtsumme
  const totalPenaltyFee = totalPenaltyFeeElite + totalPenaltyFeeBasic;
  
  return {
    totalPenaltyFeeElite,
    totalPenaltyFeeBasic,
    totalPenaltyFee
  };
}

// 🔹 Frontend-spezifische All-in-One Funktion
export function calculateFrontendPenaltyFees(
  athletes: AthleteRegistration[],
  judges: JudgeRegistration[],
  competitionGroups: CompetitionGroup[],
  judgeRules: JudgeRule[],
  penaltyFees: PenaltyFees,
  competitionData?: any // falls null-check nötig
): PenaltyCalculationResult {
  if (!competitionData) {
    return {
      totalPenaltyFeeElite: 0,
      totalPenaltyFeeBasic: 0,
      totalPenaltyFee: 0
    };
  }
  
  // Zähle Athleten und Kampfrichter
  const athleteData = countAthletesAndJudges(athletes, judges, competitionGroups);

  // Berechne Strafgebühren
  return calculateAllPenaltyFees(athleteData, judgeRules, penaltyFees, competitionData.penaltyFeeCaps);
}